

import { Link, useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useSellersQuery } from "../services/useSellersQuery";
import { Button, EmptyState, LoadingSkeleton, ErrorState } from "@a2z/ui";

type SellersPageProps = {
  locale: string;
  limit: number;
  offset: number;
};

export function SellersPage({
  locale,
  limit,
  offset,
}: SellersPageProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useSellersQuery({ limit, offset });

  if (isLoading) {
    return <LoadingSkeleton variant="list" rows={5} />;
  }

  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  return (
    <section className="flex w-full max-w-4xl flex-col gap-5 lg:px-6 lg:py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.admin.sellersTitle)}
          </h1>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => navigate(`/${locale}/admin/sellers/new`)}
        >
          {t(keys.common.create)}
        </Button>
      </div>
      {data.items.length === 0 ? (
        <EmptyState
          title={t(keys.common.emptyTitle)}
          description={t(keys.admin.sellersEmptySubtitle)}
        />
      ) : (
        <div className="grid gap-3">
          {data.items.map((seller) => (
            <Link
              key={seller.id}
              to={`/${locale}/admin/sellers/${seller.id}`}
              className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm transition hover:bg-[color:var(--hover)]"
            >
              <div className="grid gap-2 sm:grid-cols-3 sm:items-center sm:gap-4">
                <div>
                  <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                    {seller.name}
                  </div>
                  <div className="text-xs text-[color:var(--text-muted)] sm:hidden">
                    {seller.email}
                  </div>
                  <div className="text-xs text-[color:var(--text-muted)] sm:hidden">
                    {seller.phone}
                  </div>
                </div>
                <div className="hidden text-xs text-[color:var(--text-muted)] sm:flex sm:flex-col sm:gap-1">
                  <span>{seller.email}</span>
                  <span>{seller.phone}</span>
                </div>
                <div className="text-xs text-[color:var(--text-disabled)] sm:text-[color:var(--text-muted)]">
                  {seller.stores.map((store) => store.storeName).join(" • ")}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
